import { useEffect, useMemo } from 'react';
import useApiClient from 'hooks/use-api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import {
  fetchAllPages,
  normalizeTableRowData,
  platformExecuteAdHocQuery,
  platformListV2SyncIntegrations
} from '@cloudquery/api-platform-multi-tenant';
import { useCurrentOrganizationId } from 'hooks/use-current-organization-id';
import { usePayGateFeature } from 'hooks/use-pay-gate-feature';
import type {
  CloudQueryConnectStateRead,
  CloudQueryConnectStatus,
  CloudQueryIntegrationUpsertRequest,
  CloudQuerySetupState
} from 'types/api.types';
import { buildAssetInventoryUrl, buildInsightUrl, toProviderChips } from 'utils/cloudquery.utils';

const SQL_PAGE_SIZE = 500;

/* Provisioning finishes on CloudQuery's clock - the user is on another tab finishing signup, and the
   callback lands with no event this app can subscribe to. A pending handoff is the only status that
   can change while nobody touches this screen, so it is the only one that polls. */
const CONNECT_POLL_INTERVAL_MS = 3000;

/* A `stuck` setup operation is Cloud's provisioning job being restarted by its sweep; nothing the
   user does moves it, so the screen only has to notice when it eventually resolves. */
const SETUP_STUCK_POLL_INTERVAL_MS = 30000;

/* Providers are added in the CloudQuery tab, so this app only learns about one by asking again. Slower
   than the handoff poll because the user is doing multi-minute work over there, not waiting here. */
const PROVIDERS_POLL_INTERVAL_MS = 15000;

const PROVIDERS_PAGE_SIZE = 100;

// Memoized so a key can be an effect dependency: a fresh array every render would re-run any effect
// that watches one, and a refetch inside such an effect would loop.
export const useCacheKeys = () => {
  const orgId = useCurrentOrganizationId();

  return useMemo(
    () => ({
      integration: [orgId, 'cloudquery', 'integration'] as const,
      connect: [orgId, 'cloudquery', 'connect'] as const,
      setupStatus: [orgId, 'cloudquery', 'setup-status'] as const,
      syncIntegrations: [orgId, 'cloudquery', 'sync-integrations'] as const,
      sql: (query: string) => [orgId, 'cloudquery', 'sql', query] as const,
      assetInsights: (platformId: string) => [orgId, 'cloudquery', 'asset-insights', platformId] as const
    }),
    [orgId]
  );
};

export const useIsCloudQueryEnabled = () => usePayGateFeature('cloudquery');

export const useGetCloudQueryIntegration = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const apiClient = useApiClient();
  const { integration } = useCacheKeys();
  const { activated } = useIsCloudQueryEnabled();

  return useQuery({
    queryKey: integration,
    queryFn: ({ queryKey: [orgId] }) => apiClient.cloudQuery.getCloudQueryIntegration(orgId),
    enabled: activated && enabled
  });
};

export const useGetCloudQueryConnectState = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const apiClient = useApiClient();
  const { connect } = useCacheKeys();
  const { activated } = useIsCloudQueryEnabled();

  return useQuery({
    queryKey: connect,
    queryFn: ({ queryKey: [orgId] }) => apiClient.cloudQuery.getCloudQueryConnectState(orgId),
    enabled: activated && enabled,
    refetchInterval: query => (query.state.data?.status === 'pending' ? CONNECT_POLL_INTERVAL_MS : false)
  });
};

/**
 * The connect state is derived from the handoff row alone (`functions/get-connect-state.ts`), so it
 * still reports `connected` after the integration is unlinked, and it flips to `connected` on a poll
 * the integration read knows nothing about. Both follow from the same fact: the integration record is
 * what "connected" means, and this re-reads it the moment the handoff says provisioning finished.
 */
export const useRefreshIntegrationOnceConnected = (status?: CloudQueryConnectStatus) => {
  const queryClient = useQueryClient();
  const { integration } = useCacheKeys();

  useEffect(() => {
    if (status !== 'connected') return;

    queryClient.refetchQueries({ queryKey: integration });
  }, [status, integration, queryClient]);
};

/* The read cache is one entry for both flows: everything the setup endpoints answer is written into
   `connect`, so the card keeps reading a single state whichever flow produced it. */
const toReadable = (state: CloudQuerySetupState): CloudQueryConnectStateRead => {
  const { signupUrl: _signupUrl, ...readable } = state;
  return readable;
};

const setupPollInterval = (state?: CloudQuerySetupState) => {
  if (state?.status !== 'pending') return false;
  return state.provisioningState === 'stuck' ? SETUP_STUCK_POLL_INTERVAL_MS : CONNECT_POLL_INTERVAL_MS;
};

/**
 * Polls `cloudquery/setup/status` while the setup screens are open (design §9 step 2) and mirrors each
 * answer into the connect cache. Callers disable the connect-state poll meanwhile so the two never race.
 */
export const useCloudQuerySetupStatus = ({ enabled }: { enabled: boolean }) => {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const { setupStatus, connect } = useCacheKeys();
  const { activated } = useIsCloudQueryEnabled();

  const query = useQuery({
    queryKey: setupStatus,
    queryFn: ({ queryKey: [orgId] }) => apiClient.cloudQuery.getCloudQuerySetupStatus(orgId),
    enabled: activated && enabled,
    refetchInterval: query => setupPollInterval(query.state.data)
  });

  useEffect(() => {
    if (!query.data) return;

    queryClient.setQueryData(connect, toReadable(query.data));
  }, [query.data, connect, queryClient]);

  return query;
};

export const useSetupCloudQuery = () => {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const organizationId = useCurrentOrganizationId();
  const { connect, setupStatus, integration } = useCacheKeys();

  return useMutation({
    mutationFn: () => apiClient.cloudQuery.setupCloudQuery(organizationId),
    onSuccess: (state: CloudQuerySetupState) => {
      queryClient.setQueryData(connect, toReadable(state));
      queryClient.setQueryData(setupStatus, state);
      queryClient.refetchQueries({ queryKey: integration });
    }
  });
};

export const useSetupCloudQueryLogin = () => {
  const apiClient = useApiClient();
  const organizationId = useCurrentOrganizationId();

  return useMutation({ mutationFn: () => apiClient.cloudQuery.setupCloudQueryLogin(organizationId) });
};

export const useSetupCloudQueryPassword = () => {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const organizationId = useCurrentOrganizationId();
  const { connect, setupStatus } = useCacheKeys();

  return useMutation({
    mutationFn: (password: string) => apiClient.cloudQuery.setupCloudQueryPassword(organizationId, password),
    onSuccess: (state: CloudQuerySetupState) => {
      queryClient.setQueryData(connect, toReadable(state));
      queryClient.setQueryData(setupStatus, state);
    }
  });
};

export const useCloudQuerySyncIntegrations = ({ enabled }: { enabled: boolean }) => {
  const { syncIntegrations } = useCacheKeys();

  const { data, isPending, isError } = useQuery({
    queryKey: syncIntegrations,
    enabled,
    queryFn: () => platformListV2SyncIntegrations({ per_page: PROVIDERS_PAGE_SIZE }).then(({ data }) => data),
    refetchInterval: PROVIDERS_POLL_INTERVAL_MS
  });

  return useMemo(
    () => ({ providers: toProviderChips(data?.items ?? []), isPending: enabled && isPending, isError }),
    [data, enabled, isPending, isError]
  );
};

export const useUpdateCloudQueryIntegration = () => {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const { integration } = useCacheKeys();

  return useMutation({
    mutationFn: (payload: CloudQueryIntegrationUpsertRequest) =>
      apiClient.cloudQuery.updateCloudQueryIntegration(payload).then(({ data }) => data),
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: integration });
    }
  });
};

export const useCloudQueryConnection = ({ enabled = true }: { enabled?: boolean } = {}): {
  isConnected: boolean;
  isPending: boolean;
  isError: boolean;
  tenantUrl?: string;
} => {
  const { activated, isLoading: isLoadingPayGate } = useIsCloudQueryEnabled();
  const { data: integration, isSuccess, isError } = useGetCloudQueryIntegration({ enabled });

  if (!enabled) return { isConnected: false, isPending: false, isError: false };
  const tenantUrl = integration?.tenantUrl;

  /* A failed read never becomes successful, so `!isSuccess` alone leaves consumers pending forever.
     A failure is reported as such instead: "we don't know" is neither connected nor still loading, and
     callers decide what to show - see ConnectCloudQueryCta, which must not prompt anyone to connect on
     the strength of a request that merely failed. An org without the pay gate never fires the read at
     all, so it settles as not connected rather than staying pending. */
  if (!activated) {
    return { isConnected: false, isPending: isLoadingPayGate, isError: false };
  }

  return { isConnected: Boolean(tenantUrl), isPending: !isSuccess && !isError, isError, tenantUrl };
};

export const useCloudQueryLinks = (): {
  assetUrl: (platformId: string) => string | undefined;
  insightUrl: (insightId: string) => string | undefined;
} => {
  const { tenantUrl } = useCloudQueryConnection();

  return useMemo(
    () => ({
      assetUrl: (platformId: string) => (tenantUrl ? buildAssetInventoryUrl(tenantUrl, platformId) : undefined),
      insightUrl: (insightId: string) => (tenantUrl ? buildInsightUrl(tenantUrl, insightId) : undefined)
    }),
    [tenantUrl]
  );
};

export const useCloudQuerySqlAllResults = <TRow extends Record<string, unknown>>({
  query,
  enabled
}: {
  query: string;
  enabled: boolean;
}): { rows: TRow[]; isPending: boolean; isError: boolean; errorStatus?: number } => {
  const { sql } = useCacheKeys();

  const { data, isPending, isError, error } = useQuery({
    queryKey: sql(query),
    enabled,
    queryFn: () =>
      fetchAllPages<TRow>(page =>
        platformExecuteAdHocQuery({ query }, { per_page: SQL_PAGE_SIZE, page }).then(
          ({ data: { data, metadata } }) => ({
            items: normalizeTableRowData<TRow>(data),
            metadata
          })
        )
      )
  });

  return {
    rows: data?.items ?? [],
    isPending: enabled && isPending,
    isError,
    errorStatus: (error as AxiosError | null)?.response?.status
  };
};

export const useDeleteCloudQueryIntegration = () => {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const { integration, connect } = useCacheKeys();

  return useMutation({
    mutationFn: (id: string) => apiClient.cloudQuery.deleteCloudQueryIntegration(id).then(({ data }) => data),
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: integration });
      queryClient.refetchQueries({ queryKey: connect });
    }
  });
};
